import React from 'react';
import { Box, ButtonBase, Paper, Typography } from '@mui/material';
import { InterventionLadderStepModel } from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatNumber } from '../../../utilities';
import { endedColor, highlightColor, moveDownColor, moveUpColor, stepColor, stepIntensity, stepMarkerBorders } from '../intervention-ladder/intervention-ladder-colors';
import { StepCardHeading, stepCardColors, stepCardDivider, stepCardFrame, StepNumber } from '../intervention-ladder/intervention-ladder-step-card';
import { InterventionMovesStaircase, stepRange } from './intervention-moves-staircase';

const cardShadow = '0 2px 6px rgba(24, 76, 97, 0.12)';

export interface InterventionMovesComponentProps {
  steps: InterventionLadderStepModel[] | undefined;
  selectedStep: number;
  periodLabel: string;
  onSelectStep: (step: InterventionLadderStepModel, intensity: number) => void;
}

export function InterventionMovesComponent({ steps, selectedStep, periodLabel, onSelectStep }: InterventionMovesComponentProps) {
  const selectedIndex = steps?.findIndex(step => step.step === selectedStep) ?? -1;
  if (steps !== undefined && selectedIndex < 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h2" sx={{ fontSize: '1rem', mb: 0.5 }}>
        Vandringer mellem trin
      </Typography>
      {steps === undefined ? <CircularProgressCentered /> : <InterventionMoves steps={steps} selectedIndex={selectedIndex} periodLabel={periodLabel} onSelectStep={onSelectStep} />}
    </Paper>
  );
}

interface InterventionMovesProps {
  steps: InterventionLadderStepModel[];
  selectedIndex: number;
  periodLabel: string;
  onSelectStep: (step: InterventionLadderStepModel, intensity: number) => void;
}

function InterventionMoves({ steps, selectedIndex, periodLabel, onSelectStep }: InterventionMovesProps) {
  const step = steps[selectedIndex];
  const intensity = stepIntensity(selectedIndex, steps.length);
  const lowerSteps = steps.slice(0, selectedIndex);
  const higherSteps = steps.slice(selectedIndex + 1);

  function selectStepAt(index: number) {
    onSelectStep(steps[index], stepIntensity(index, steps.length));
  }

  function onSelectLower() {
    selectStepAt(selectedIndex - 1);
  }

  function onSelectHigher() {
    selectStepAt(selectedIndex + 1);
  }

  return (
    <>
      <Typography sx={{ fontSize: '0.8125rem', fontStyle: 'italic', color: 'text.secondary' }}>
        Klik et trin for at se hvor mange borgere der er flyttet væk fra det · {periodLabel}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, mb: 2, fontSize: '0.75rem', color: 'text.secondary' }}>
        <span>
          <Arrow color={moveDownColor}>↓</Arrow> Ned ad trappen
        </span>
        <span>
          <Arrow color={moveUpColor}>↑</Arrow> Op ad trappen
        </span>
      </Box>
      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', '& strong': { color: '#16323d', fontWeight: 600 } }}>
        Fra <strong>{step.title}</strong> (Trin {step.step}) er{' '}
        <Box component="span" sx={{ color: highlightColor, fontSize: '0.9375rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
          {formatNumber(step.departures)}
        </Box>{' '}
        borgere flyttet videre
      </Typography>
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <InterventionMovesStaircase steps={steps} selectedIndex={selectedIndex} onSelectLower={onSelectLower} onSelectHigher={onSelectHigher} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2, alignItems: 'start', mt: 0.75 }}>
          <Box>
            {lowerSteps.length > 0 && (
              <MoveCard
                direction="Ned ad trappen"
                arrow="↓"
                color={moveDownColor}
                range={stepRange(lowerSteps)}
                stripe={stepColor(stepMarkerBorders, stepIntensity(selectedIndex - 1, steps.length))}
                count={step.moves.down}
                label="gik ned"
                onSelect={onSelectLower}
              />
            )}
          </Box>
          <Box sx={{ ...stepCardFrame(true), ...stepCardColors(intensity), px: 1.5 }}>
            <Box sx={{ py: 1.5 }}>
              <StepCardHeading step={step} isSelected />
            </Box>
            <Box sx={{ ...stepCardDivider(intensity), py: 1.5, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', alignItems: 'start', gap: 1 }}>
              <StepNumber value={formatNumber(step.moves.stayed)} label="blev på trinnet" />
              <StepNumber value={formatNumber(step.departures)} label="flyttet væk" />
            </Box>
          </Box>
          <Box>
            {higherSteps.length > 0 && (
              <MoveCard
                direction="Op ad trappen"
                arrow="↑"
                color={moveUpColor}
                range={stepRange(higherSteps)}
                stripe={stepColor(stepMarkerBorders, stepIntensity(selectedIndex + 1, steps.length))}
                count={step.moves.up}
                label="gik op"
                onSelect={onSelectHigher}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1.25 }}>
          <svg width="20" height="26" viewBox="0 0 20 26" aria-hidden>
            <path d="M10,0 L10,17" stroke={endedColor} strokeWidth={2.5} />
            <polygon points="10,25 4,15 16,15" fill={endedColor} />
          </svg>
          <Typography variant="overline" sx={{ lineHeight: 1.4, fontWeight: 700, color: endedColor }}>
            Afsluttet · {formatNumber(step.moves.ended)} borgere
          </Typography>
        </Box>
      </Box>
    </>
  );
}

interface MoveCardProps {
  direction: string;
  arrow: string;
  color: string;
  range: string;
  stripe: string;
  count: number;
  label: string;
  onSelect: () => void;
}

function MoveCard({ direction, arrow, color, range, stripe, count, label, onSelect }: MoveCardProps) {
  return (
    <ButtonBase
      component="div"
      onClick={onSelect}
      aria-pressed={false}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        textAlign: 'left',
        font: 'inherit',
        borderRadius: '10px',
        borderTop: `4px solid ${stripe}`,
        boxShadow: cardShadow,
        bgcolor: 'background.paper',
        color: '#16323d',
        px: 1.75,
        pt: 1.25,
        pb: 1.5,
      }}
    >
      <Box>
        <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.4, color: 'text.secondary' }}>
          <Arrow color={color}>{arrow}</Arrow> {direction}
        </Typography>
        <Typography variant="subtitle2" component="div" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
          Trin {range}
        </Typography>
      </Box>
      <Box sx={{ flex: 'none', textAlign: 'right', color }}>
        <KeyNumber value={count} label={label} />
      </Box>
    </ButtonBase>
  );
}

interface KeyNumberProps {
  value: number;
  label: string;
}

function KeyNumber({ value, label }: KeyNumberProps) {
  return (
    <Box sx={{ lineHeight: 1 }}>
      <Box sx={{ fontSize: '1.25rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(value)}</Box>
      <Box sx={{ fontSize: '0.6rem', opacity: 0.66, mt: 0.25 }}>{label}</Box>
    </Box>
  );
}

interface ArrowProps {
  color: string;
  children: string;
}

function Arrow({ color, children }: ArrowProps) {
  return (
    <Box component="span" sx={{ color, fontWeight: 700 }}>
      {children}
    </Box>
  );
}
